// LineChartView — recharts LineChart wrapper with a11y hidden data table.
// Purely presentational: no data fetching, no i18n, no business logic.
// All labels are passed by callers.
import type { CSSProperties } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { keyColor } from './chartPalette.js';

export interface LineDataPoint {
  /** Category label on the x-axis (e.g. "2025-01"). */
  x: string;
  y: number;
}

export interface LineSeriesSpec {
  key: string;
  label: string;
  data: LineDataPoint[];
}

export interface LineChartViewProps {
  /** One or more named series; each is `{ key, label, data[] }`. */
  series: LineSeriesSpec[];
  /** Used as the data table's first column header. */
  xAxisLabel: string;
  /** Accessible label for the chart as a whole. */
  ariaLabel: string;
  height?: number;
  /**
   * Optional numeric value formatter applied to the Y axis ticks, the tooltip
   * value, AND the hidden data table's value cells, so money/units stay
   * consistent across the visual and accessible surfaces. When omitted, raw
   * numbers render unchanged (backward-compatible).
   */
  valueFormatter?: (value: number) => string;
}

// Visually hidden but accessible to AT — the deterministic unit-test hook.
const srOnly: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
};

const axisTickStyle = { fontSize: 11, fill: 'var(--fg-3)' };
const gridStyle = { stroke: 'var(--border-1)', strokeDasharray: '3 3' };

// WHY private col ids: a series with key === "x" would overwrite the category
// field in the recharts row object, corrupting the x-axis. Internal ids are
// index-based (s0, s1, …) and the category uses __x — both are unreachable by
// arbitrary public series keys.
const colId = (i: number) => `s${i}`;
const CAT_KEY = '__x';

/**
 * Merge multi-series data into recharts' row-per-x-value format.
 * Each row is `{ __x: category, s0: y0, s1: y1, ... }` — internal ids only,
 * decoupled from the public series keys to prevent collision.
 */
function mergeSeriesData(series: LineSeriesSpec[]): Record<string, unknown>[] {
  // WHY: no .sort() — Set preserves first-seen insertion order, which is the
  // caller's intended category order. Sorting would corrupt non-lexical sequences
  // like Q1/Q2/Q10 or localized month names (F-007).
  const allX = [...new Set(series.flatMap((s) => s.data.map((d) => d.x)))];
  return allX.map((x) => {
    const row: Record<string, unknown> = { [CAT_KEY]: x };
    series.forEach((s, i) => {
      const point = s.data.find((d) => d.x === x);
      row[colId(i)] = point !== undefined ? point.y : null;
    });
    return row;
  });
}

export function LineChartView({
  series,
  xAxisLabel,
  ariaLabel,
  height = 280,
  valueFormatter,
}: LineChartViewProps) {
  const data = mergeSeriesData(series);
  // Collect all unique x-categories in caller order for the table rows.
  const allX = (data as Array<Record<string, string>>).map((r) => r[CAT_KEY]);

  // Conditional, exactOptionalPropertyTypes-safe extras: only set the recharts
  // formatter props when a `valueFormatter` is supplied (otherwise unchanged).
  const yAxisExtra = valueFormatter
    ? { tickFormatter: (value: number) => valueFormatter(value) }
    : {};
  const tooltipExtra = valueFormatter
    ? {
        formatter: (value: unknown): string =>
          typeof value === 'number' ? valueFormatter(value) : String(value),
      }
    : {};
  const formatCell = (value: number): string =>
    valueFormatter ? valueFormatter(value) : String(value);

  return (
    <div>
      {/* Chart SVG is a decorative visual; the data table below is the a11y alternative */}
      <div aria-hidden="true">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data}>
            <CartesianGrid {...gridStyle} />
            <XAxis dataKey={CAT_KEY} tick={axisTickStyle} />
            <YAxis tick={axisTickStyle} {...yAxisExtra} />
            <Tooltip {...tooltipExtra} />
            <Legend />
            {series.map((s, i) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={colId(i)}
                name={s.label}
                stroke={keyColor(s.key)}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Visually-hidden data table: a11y text alternative + test hook */}
      <table style={srOnly} aria-label={ariaLabel}>
        <thead>
          <tr>
            <th scope="col">{xAxisLabel}</th>
            {series.map((s) => (
              <th key={s.key} scope="col" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allX.map((x) => (
            <tr key={x}>
              <td>{x}</td>
              {series.map((s) => {
                const point = s.data.find((d) => d.x === x);
                return (
                  <td key={s.key} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {point !== undefined ? formatCell(point.y) : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
